import { Feature, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerColors } from '../../models/layer-colors.model';
import { GeometryTypes } from '../../models/layer-renderer.model';
import { DatasetZIndexValues } from 'src/app/modules/cop/drawing/models/dataset-zIndex.model';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { MapLabelService } from '../../label.service';
import { GeoJsonUtilityService } from '../../geo-json-utility.service';
import { TropicalCycloneService } from '../../../services/layers/tropical-cyclones-service';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerClusterService } from '../../cluster.service';
import { PopupService } from '../../popups/popup.service';

export const SELECTED_Z_INDEX = 10000;

export class AlertTropicalCycloneFeature implements Feature {
  private googleMapFeatures: GoogleMapFeatures;
  private documentHash: number = 0;
  private geometryHash: number = 0;
  private isVisible: boolean = false;
  private isSelected: boolean = false;
  private layerColors: LayerColors;
  private zIndex: number = 0;
  private mouseoutTimers = {};
  private layerUId: string;
  private datasetUId: string;
  private documentUId: string;
  private minLabelZoomLevel: number;
  private layerHasLabels: boolean;
  private labelService: MapLabelService;
  private clusterService: MapLayerClusterService;
  private timerId: string | number | NodeJS.Timeout;
  private popup: PopupService;
  private canShowPopup: boolean;

  constructor(private labelValues: { [key: string]: string }) {}

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;

    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);

    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.popup = context.popup;
    this.canShowPopup = context.popup?.isEnabledForLayer(layer);
    this.minLabelZoomLevel = this.labelService.getLayerLabelMinZoom(layer);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let workflowColor: string = null;
    if (context.isWorkflowLayerSection) {
      workflowColor = FeatureRenderingUtilityService.getDocumentWorkflowColor(dataset, document);
    }

    const fillColor = workflowColor ?? LayerColorsService.getLayerFillColor(document.fill_color ?? layer.color);
    const borderColor = LayerColorsService.getLayerBorderColorFromFillColor(fillColor);
    this.layerColors = {
      fillColor,
      borderColor
    };

    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];

    // build and check the document hash...if the values have not changed then
    // bail out on the rendering process.
    const newHash = this.buildDocumentHash(dataset, layer, document, this.isSelected);
    if (this.documentHash === newHash) {
      return;
    }
    this.documentHash = newHash;

    // check to see if the geometry has changed. If so then we need to remove all existing google map object
    // and redraw. If not then update the colors and icons in the case the workflow has been updated.
    const geoField = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);
    const geoJsonValue = document[geoField];
    const newGeoHash = this.buildGeometryHash(geoJsonValue ?? '');
    if (newGeoHash !== this.geometryHash) {
      this.destroy();

      // get the geometry features from the standard geo field in the document
      // and then render each to the map
      this.addGoogleMapObjects(context, layer, dataset, document);

      this.geometryHash = newGeoHash;
    } else {
      // this is an update where the geometry has not changed. just update the geometry colors and marker icons
      this.updateGoogleMapObjects(context, layer, dataset, document);
    }
  }

  setVisible() {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
      this.clusterService.registerMarkerAdd(this.layerUId, marker);
    }
    if (this.layerHasLabels === true) {
      this.showLabel();
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setVisible(true);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(true);
    }
  }

  setHidden(): void {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
      if (labelOverlay) {
        labelOverlay.setVisible(false);
      }
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
    }
    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setVisible(false);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(false);
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setSelected() {
    this.isSelected = true;

    this.setMarkerSelected();
  }

  setUnselected() {
    this.isSelected = false;

    this.setMarkerNormal();
  }

  showLabel(): void {
    this.layerHasLabels = true;
    if (this.googleMapFeatures.markers.length === 0) {
      return;
    }

    for (let marker of this.googleMapFeatures.markers) {
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);

      if (!labelOverlay) {
        const parentGeometry = marker['__data'].parentGeometry;
        const name = marker['__data'].name;
        const isTrackHead = marker['__data'].isTrackHead;
        const isForectsHead = marker['__data'].isForectsHead;

        if (!!parentGeometry /* && (isTrackHead === true || isForectsHead === true)*/) {
          const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);
          const label = this.getLabelDisplay(parentGeometry, name, markerUrls.normal, this.labelValues);
          labelOverlay = this.labelService.createLabel(
            marker,
            label,
            this.layerUId,
            this.documentUId,
            {
              left: null,
              top: -135
            },
            'cyclon-marker-label',
            10000
          );
          this.labelService.addMarkerLabel(this.layerUId, this.minLabelZoomLevel, labelOverlay);
        }
      } else {
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      }
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
    }
  }

  private updateLabel(layer: Layer, marker: any): void {
    const parentGeometry = marker['__data'].parentGeometry;
    const name = marker['__data'].name;
    const isTrackHead = marker['__data'].isTrackHead;
    const isForectsHead = marker['__data'].isForectsHead;

    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    const hasLabel =
      layer.displayOptions?.labelsVisible === true && !!parentGeometry; /* && (isTrackHead === true || isForectsHead === true)*/

    let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
    if (hasLabel) {
      if (labelOverlay) {
        const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);
        const label = this.getLabelDisplay(parentGeometry, name, markerUrls.normal, this.labelValues);
        labelOverlay.setLabel(label);
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.showLabel();
      }
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
      marker.unbindAll();
      marker.setMap(null);
    }
    this.googleMapFeatures.markers = [];

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.unbindAll();
      polygon.setMap(null);
    }
    this.googleMapFeatures.polygons = [];

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.unbindAll();
      polyline.setMap(null);
    }
    this.googleMapFeatures.polylines = [];
  }

  addGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document) {
    const geoFeatures = this.getGeometryFeaturesForDisplay(dataset, layer, document);
    if (!geoFeatures) {
      return;
    }

    for (let geo of geoFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo);
    }
  }

  addGoogleMapObject(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    if (geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, null, geometry);
    } else if (geometry.type === GeometryTypes.polygon || geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry);
    } else if (geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry);
    } else if (geometry.type === GeometryTypes.feature) {
      this.addFeature(context, layer, dataset, document, geometry);
    }
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, parentGeometry: any, geometry: any) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const isTrackHead = parentGeometry?.isTrackHead === true;
    const isForectsHead = parentGeometry?.isForectsHead === true;

    const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);

    const anchor = isTrackHead === true ? new google.maps.Point(16, 16) : new google.maps.Point(11, 11);
    const size = isTrackHead === true ? new google.maps.Size(32, 32) : new google.maps.Size(22, 22);

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? markerUrls.normal : markerUrls.selected,
        anchor,
        size
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      documentUId: document.uid,
      name: document.name,
      isTrackHead: isTrackHead,
      isForectsHead: isForectsHead,
      parentGeometry: parentGeometry
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: 72,
          offsetBelow: 23,
          layer,
          dataset,
          document
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const polygon = new google.maps.Polygon({
      paths: coords,
      strokeColor: this.layerColors.borderColor,
      fillColor: this.layerColors.fillColor,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
      fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polygon['__data'] = {};

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polygons.push(polygon);

    polygon.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });
  }

  addPolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path: geometry.coordinates?.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor: this.layerColors.borderColor,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 2,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polyline['__data'] = {};

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addFeature(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any): void {
    if (geometry.geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry, geometry.geometry);
    } else if (geometry.geometry.type === GeometryTypes.polygon || geometry.geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry.geometry);
    } else if (geometry.geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry.geometry);
    } else {
    }
  }

  updateGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): void {
    for (let marker of this.googleMapFeatures.markers) {
      const icon = marker.getIcon() as google.maps.Icon;

      marker['__data'].name = document.name;
      const parentGeometry = marker['__data'].parentGeometry;
      const isTrackHead = marker['__data'].isTrackHead;
      const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);
      icon.url = this.isSelected ? markerUrls.selected : markerUrls.normal;
      marker.setIcon(icon);

      this.updateLabel(layer, marker);
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });
    }

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }
  }

  buildMarkerImageUrls(isTrackHead: boolean, parentGeometry: any): MarkerUrls {
    const imageName =
      isTrackHead === true
        ? TropicalCycloneService.getCycloneImageForCategory(
            !!parentGeometry && !!parentGeometry.properties?.stormCat ? parentGeometry?.properties.stormCat : 'H5'
          )
        : 'tropical-cyclone_marker';

    const fillColor =
      !!parentGeometry && !!parentGeometry.properties?.stormCat
        ? TropicalCycloneService.getCycloneColorForCategory(parentGeometry.properties.stormCat)
        : this.layerColors.fillColor;

    const backColor = fillColor.replace('#', '');

    const layerIconUrl = `/api/v1/meta/markers/${imageName}/${backColor}/0/0/default/${imageName}.png`;
    const layerIconSelectedUrl = `/api/v1/meta/markers/${imageName}/${backColor}/0/0/selected/${imageName}.png`;

    return {
      normal: layerIconUrl,
      selected: layerIconSelectedUrl
    };
  }

  buildDocumentHash(dataset: Dataset, layer: Layer, document: Document, isSelected: boolean): number {
    const geometryFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

    const geometry = document[geometryFieldName] ?? '';
    const workflowStepUId = document.workflow_step_uid;
    const fillColor = document.fill_color ?? '';
    const borderColor = document.border_color ?? '';
    const name = document.name ?? '';

    const stringToHash = [geometry, workflowStepUId, fillColor, borderColor, isSelected, name].join();

    const hash = cyrb53(stringToHash);

    return hash;
  }

  buildGeometryHash(geometry: string): number {
    const hash = cyrb53(geometry);
    return hash;
  }

  getBadgeCount(dataset: Dataset, document: Document): number | null {
    if (dataset.type !== DatasetType.Incident) {
      return null;
    }

    const field = dataset.schema?.fields.find(
      f => f.dataType === FieldDataType.RelatedParent && f.systemName?.startsWith('alerts_') && document[f.systemName] !== undefined
    );
    if (!!!field) {
      return null;
    }

    const value = document[field.systemName];

    return !isNaN(value) ? value : null;
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
        clearTimeout(that.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;
      //      const markerUrls = marker['__data'].markerUrls;
      const isTrackHead = marker['__data'].isTrackHead;
      const parentGeometry = marker['__data'].parentGeometry;
      const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);

      const anchor = isTrackHead === true ? new google.maps.Point(16, 16) : new google.maps.Point(11, 11);
      const size = isTrackHead === true ? new google.maps.Size(32, 32) : new google.maps.Size(22, 22);

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: markerUrls.selected,
        anchor,
        size
      });
    }
  }

  setMarkerNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);
      const isTrackHead = marker['__data'].isTrackHead;
      const parentGeometry = marker['__data'].parentGeometry;
      const markerUrls = this.buildMarkerImageUrls(isTrackHead, parentGeometry);

      const anchor = isTrackHead === true ? new google.maps.Point(16, 16) : new google.maps.Point(11, 11);
      const size = isTrackHead === true ? new google.maps.Size(32, 32) : new google.maps.Size(22, 22);

      marker.setIcon({
        url: markerUrls.normal,
        anchor,
        size
      });
    }
  }

  getGeometryFeaturesForDisplay(dataset: Dataset, layer: Layer, document: Document): any[] {
    const geoFields = this.getGeometryFieldSystemNames(dataset);

    const allFeatures: any[] = [];

    if (geoFields.length == 0) {
      return allFeatures;
    }

    geoFields.forEach(geoField => {
      const fieldValueJson = document[geoField];
      if (!!fieldValueJson) {
        let fieldValue: any = null;
        try {
          fieldValue = JSON.parse(fieldValueJson);
        } catch {}

        if (!!fieldValue) {
          const features = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(fieldValue);
          if (!!features) {
            if (geoField === 'track' && document.is_active && features.length) {
              features[features.length - 1].isTrackHead = true;
            }
            if (geoField === 'forecast' && document.is_active && features.length) {
              features[features.length - 1].isForecastHead = true;
            }
            allFeatures.push(...features);
          }
        }
      }
    });

    return allFeatures;
  }

  private getGeometryFieldSystemNames(dataset: Dataset): string[] {
    const fieldNames = [];

    let geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Geometry);

    if (!!geoField) {
      fieldNames.push(geoField.systemName);
    }

    geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Text && f.systemName === 'track');
    if (!!geoField) {
      fieldNames.push(geoField.systemName);
    }

    geoField = dataset.schema.fields.find(f => f.dataType === FieldDataType.Text && f.systemName === 'forecast');
    if (!!geoField) {
      fieldNames.push(geoField.systemName);
    }

    return fieldNames;
  }

  private getLabelDisplay(geometry: any, name: string, layerIconUrl: string, labelValues: { [key: string]: string }): string {
    const cultureCode = labelValues['cultureCode'] ?? 'en-US';
    const date = new Date(geometry.properties.timestamp);
    const dayAndTime = `${date
      .toLocaleDateString(cultureCode, { weekday: 'short', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' })
      .replace(',', '')
      .toUpperCase()}`;

    const kmUnits = labelValues['distanceUnit'] === 'kilometers';

    const stormCat = geometry.properties.stormCat || 'S';
    const windspeed = (kmUnits ? geometry.properties.windSpeedKPH : geometry.properties.windSpeedMPH) || '';
    const gustsspeed = (kmUnits ? geometry.properties.gustSpeedKPH : geometry.properties.gustSpeedMPH) || '';
    const movingspeed = (kmUnits ? geometry.properties.movement?.speedKPH : geometry.properties.movement?.speedMPH) || '';
    const direction = geometry.properties.movement?.direction || '';

    const windspeedUnits = kmUnits ? 'KPH' : 'MPH';

    let movingText = labelValues['MAP.CYCLONES.MOVING'];

    const directionText = movingText.replace('{{direction}}', direction).replace('{{speed}}', movingspeed);

    return `<div>
      <div class="title">${name}</div>
      <div class="time">${dayAndTime}</div>
      <div class="summary">
        <div style="flex-direction: row; display: flex; place-content: center flex-start; align-items: flex-start;">
          <span class="title">${labelValues['MAP.CYCLONES.CAT']}</span>
          <span class="title">${labelValues['MAP.CYCLONES.WINDS']}</span>
          <span class="title">${labelValues['MAP.CYCLONES.GUSTS']}</span>
        </div>
        <div style="flex-direction: row; display: flex; place-content: center flex-start; align-items: flex-start;">
          <div
            class="data">
            <img align="top" src="${layerIconUrl}" />
            &nbsp;${stormCat}
          </div>
          <div
            class="data">
            ${windspeed}
            <div class="units">${windspeedUnits}</div>
          </div>
          <div
            fxLayout="row"
            fxLayoutAlign="start center"
            class="data">
            ${gustsspeed}
            <div class="units">${windspeedUnits}</div>
          </div>
        </div>
      </div>
      <div class="dir">
        ${directionText} ${windspeedUnits}
      </div>
    </div>`;
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const that = this;
    this.timerId = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
      }
    }, 3000);
  }
}
